/**
 * SubscriptionStyleSections - Collapsible style sections for subscription configuration
 *
 * Renders collapsible accordions for:
 * 1. Unselected item state
 * 2. Selected item state
 * 3. Item paddings
 * 4. Column layout (leftLabelColumnWidthPercentage)
 * 5. List paddings
 * 6. Left Top Label styles
 * 7. Left Bottom Label styles
 * 8. Right Top Label styles
 * 9. Right Bottom Label styles
 * 10. Description Label styles
 * 11. Purchase Button Label styles
 * 12. Badge styles
 * 13. Checkbox styles (visible only when itemType is CheckboxLabels or LabelsCheckbox)
 *
 * Note: Uses MUI Accordion directly (not SAccordion) to allow nesting
 * inside parent SAccordion. SAccordion prevents nesting by design.
 *
 * Key Feature: Style changes to items.0.* are propagated to ALL items
 * (same pattern as ListStyleSections.tsx)
 *
 * Sprint: screen-editor-paywall
 * Task: Phase 2 - Subscriptions Section Refactoring
 */

import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PaddingElement, PaddingValues } from '../../../../factories/elements';
import { StyleFieldRow } from '../../../shared/styleFields/StyleFieldRow';
import {
  SubscriptionStyleSectionConfig,
  SubscriptionCellType,
  SubscriptionItemType,
} from '../../../../config/v2PaywallSubscriptionsConfig';
import { FIELD_STYLE_GRID_GAP } from '../../../../config/ui';
import { EXPANDED_SECTION_BGCOLOR } from '../../../../config/editorFieldStyles';
import { getStyleCheckboxTestId, getStyleInputTestId } from '../../../../utils/testIdUtils';
import { setNestedValue } from '@modules/reactFlow/utils/v2FieldOperations';

/**
 * Props for SubscriptionStyleSections
 */
export interface SubscriptionStyleSectionsProps {
  /** Style section configurations */
  sections: SubscriptionStyleSectionConfig[];

  /** Current cellType (for visibility conditions) */
  currentCellType: SubscriptionCellType;

  /** Current itemType (for checkbox styles visibility) */
  currentItemType: SubscriptionItemType;

  /** Full struct object for reading values */
  struct: Record<string, unknown>;

  /** Callback when a value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether editing is disabled */
  disabled?: boolean;

  /** Path to items array for style propagation (default: 'subscriptions.items') */
  itemsPath?: string;

  /** When true, padding reads/writes use V2 EdgeInsets (top/bottom/start/end) instead of V1 (paddingTop/Right/Bottom/Left) */
  useV2Insets?: boolean;
}

/**
 * Get value at a dot-notation path from an object
 */
function getValueAtPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Extract padding values from struct at given base path.
 * When useV2Insets=true, reads EdgeInsets fields (top/bottom/start/end).
 */
function getPaddingValues(struct: Record<string, unknown>, basePath: string, useV2Insets = false): PaddingValues {
  const styles = getValueAtPath(struct, basePath) as Record<string, unknown> | undefined;

  if (useV2Insets) {
    return {
      paddingTop: typeof styles?.top === 'number' ? styles.top : 0,
      paddingRight: typeof styles?.end === 'number' ? styles.end : 0,
      paddingBottom: typeof styles?.bottom === 'number' ? styles.bottom : 0,
      paddingLeft: typeof styles?.start === 'number' ? styles.start : 0,
    };
  }

  return {
    paddingTop: typeof styles?.paddingTop === 'number' ? styles.paddingTop : 0,
    paddingRight: typeof styles?.paddingRight === 'number' ? styles.paddingRight : 0,
    paddingBottom: typeof styles?.paddingBottom === 'number' ? styles.paddingBottom : 0,
    paddingLeft: typeof styles?.paddingLeft === 'number' ? styles.paddingLeft : 0,
  };
}

/**
 * Translate V1 padding field names to V2 EdgeInsets field names in path.
 */
function translatePaddingPath(path: string): string {
  return path
    .replace('.paddingTop', '.top')
    .replace('.paddingRight', '.end')
    .replace('.paddingBottom', '.bottom')
    .replace('.paddingLeft', '.start');
}

function cloneItems(items: unknown[]): unknown[] {
  return JSON.parse(JSON.stringify(items)) as unknown[];
}

/**
 * SubscriptionStyleSections - Renders collapsible style accordions
 *
 * Uses MUI Accordion directly to avoid SAccordion nesting restriction.
 */
export const SubscriptionStyleSections: React.FC<SubscriptionStyleSectionsProps> = ({
  sections,
  currentCellType,
  currentItemType,
  struct,
  onChange,
  disabled = false,
  itemsPath = 'subscriptions.items',
  useV2Insets = false,
}) => {
  const theme = useTheme();

  // Filter sections by visibility condition (based on both cellType and itemType)
  const visibleSections = useMemo(() => {
    return sections.filter((section) => {
      if (!section.visibilityCondition) return true;
      return section.visibilityCondition(currentCellType, currentItemType);
    });
  }, [sections, currentCellType, currentItemType]);

  /**
   * When updating item-specific styles (labels, badge),
   * propagate the change to ALL items in the subscriptions, not just item 0.
   *
   * Detects paths like 'subscriptions.items.0.leftLabelTop.styles.fontSize'
   * and writes one cloned items array with the update applied to every item.
   * This must be atomic: FlexibleLayout proxies section edits as whole-section
   * replacements, so multiple per-index writes would race and the last write wins.
   */
  const handleItemStyleChange = useCallback(
    (path: string, value: unknown) => {
      const itemZeroPrefix = `${itemsPath}.0.`;

      // Check if this is an item-specific style path for the first item.
      if (path.startsWith(itemZeroPrefix)) {
        const items = getValueAtPath(struct, itemsPath) as unknown[] | undefined;
        if (items && items.length > 0) {
          const itemRelativePath = path.slice(itemZeroPrefix.length);
          const updatedItems = cloneItems(items);

          updatedItems.forEach((item) => {
            if (!item || typeof item !== 'object') return;

            // Badge is optional. Updating badge styles must not create empty
            // visible badges on products where badge content is intentionally absent.
            if (itemRelativePath.startsWith('badge.') && !(item as Record<string, unknown>).badge) {
              return;
            }

            setNestedValue(item as Record<string, unknown>, itemRelativePath, value);
          });

          onChange(itemsPath, updatedItems);
          return;
        }
      }
      // Not an item path, update normally
      onChange(path, value);
    },
    [struct, onChange, itemsPath]
  );

  // Handle padding change - uses handleItemStyleChange to propagate to all items
  // When useV2Insets, translates paddingTop→top, paddingLeft→start, paddingRight→end, paddingBottom→bottom
  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      const translatedPath = useV2Insets ? translatePaddingPath(path) : path;
      handleItemStyleChange(translatedPath, value);
    },
    [handleItemStyleChange, useV2Insets]
  );

  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
      data-testid="subscription-style-sections"
    >
      {visibleSections.map((section) => (
        <Accordion
          key={section.id}
          defaultExpanded={section.defaultExpanded}
          disableGutters
          elevation={0}
          data-testid={`subscription-style-section-${section.id}`}
          sx={{
            backgroundColor: 'transparent',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              minHeight: 32,
              px: 1,
              py: 0,
              backgroundColor: theme.palette.action.hover,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: theme.palette.action.selected,
              },
            },
            '& .MuiAccordionSummary-content': {
              margin: '4px 0',
            },
            '& .MuiAccordionDetails-root': {
              px: 2,
              pt: 1,
              pb: 1,
              bgcolor: EXPANDED_SECTION_BGCOLOR,
              borderRadius: 1,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            aria-controls={`${section.id}-content`}
            id={`${section.id}-header`}
          >
            <Typography variant="caption" fontWeight={500}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Render fields in 2-column grid if they exist */}
            {section.fields && section.fields.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                  mb: section.paddingConfig ? 1.5 : 0,
                }}
                data-testid={`subscription-style-section-${section.id}-fields`}
              >
                {section.fields.map((fieldConfig) => (
                  <Box
                    key={fieldConfig.path}
                    sx={{ gridColumn: fieldConfig.type === 'slider' ? '1 / -1' : 'auto' }}
                  >
                    <StyleFieldRow
                      config={fieldConfig}
                      value={getValueAtPath(struct, fieldConfig.path) ?? (fieldConfig.isNullable === false ? fieldConfig.defaultValue : undefined)}
                      onChange={(value) => handleItemStyleChange(fieldConfig.path, value)}
                      disabled={disabled}
                      inputTestId={getStyleInputTestId(section.id, fieldConfig.label)}
                      checkboxTestId={getStyleCheckboxTestId(section.id, fieldConfig.label)}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Render padding element if configured (after fields) */}
            {section.paddingConfig && (
              <PaddingElement
                basePath={section.paddingConfig.basePath}
                values={getPaddingValues(struct, section.paddingConfig.basePath, useV2Insets)}
                onChange={handlePaddingChange}
                label={section.paddingConfig.label}
                disabled={disabled}
                defaultExpanded={true}
              />
            )}

            {/* Placeholder for empty sections (no fields and no padding) */}
            {(!section.fields || section.fields.length === 0) && !section.paddingConfig && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontStyle: 'italic' }}
                data-testid={`subscription-style-section-${section.id}-placeholder`}
              >
                Style fields coming soon
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default SubscriptionStyleSections;
